import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const FIELDS = [
  ['program_name', 'Program', 'text'], ['loan_type', 'Loan type', 'text'], ['min_fico', 'Min FICO', 'number'],
  ['max_ltv', 'Max LTV %', 'number'], ['max_ltc', 'Max LTC %', 'number'], ['max_ltarv', 'Max LTARV %', 'number'],
  ['min_loan_amount', 'Min loan $', 'number'], ['max_loan_amount', 'Max loan $', 'number'],
  ['interest_rate', 'Rate', 'text'], ['term_months', 'Term (mo)', 'number'], ['property_types', 'Property types', 'list'],
  ['states', 'States', 'list'], ['excluded_states', 'Excluded states', 'list'], ['min_dscr', 'Min DSCR', 'number'],
  ['min_experience', 'Min experience (#)', 'number'], ['experience_notes', 'Experience notes', 'text'], ['min_liquidity', 'Min liquidity', 'text'],
  ['points', 'Points', 'text'], ['fees', 'Fees', 'text'], ['amortization', 'Amortization', 'text'],
  ['prepayment_penalty', 'Prepay penalty', 'text'], ['recourse', 'Recourse', 'text'], ['eligibility_notes', 'Eligibility notes', 'text'], ['notes', 'Notes', 'text'],
];

const toInput = (type, v) => (v == null ? '' : type === 'list' ? (Array.isArray(v) ? v.join(', ') : v) : String(v));
const fromInput = (type, v) => {
  if (v === '' || v == null) return type === 'list' ? [] : null;
  if (type === 'number') return Number.isNaN(Number(v)) ? null : Number(v);
  if (type === 'list') return v.split(',').map((s) => s.trim()).filter(Boolean);
  return v;
};

// Review table for AI-extracted programs (PDF doc or lender Notes): edit, mark Not Confirmed, approve into the Product Matrix.
export const ProgramReviewTable = ({ lenderId, doc, onApproved, approvePath, approveBody, idSuffix }) => {
  const [rows, setRows] = useState(() => (doc.parsed_programs || doc.programs || []).map((p) => ({ ...p, _selected: true })));
  const [saving, setSaving] = useState(false);
  const sid = idSuffix || doc.doc_id;

  const update = (i, key, type, raw) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: fromInput(type, raw) } : r)));

  const approve = async () => {
    const selected = rows.filter((r) => r._selected).map(({ _selected, not_confirmed, ...p }) => p);
    if (!selected.length) return toast.error('Select at least one program to approve');
    setSaving(true);
    try {
      const res = await api.post(approvePath || `/lenders/${lenderId}/programs/approve`, { ...(approveBody || { doc_id: doc.doc_id }), programs: selected });
      toast.success(`${res.data.approved_count} program(s) added to the Product Matrix`);
      onApproved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setSaving(false);
    }
  };

  if (!rows.length) return null;
  const notConfirmedCount = rows.reduce((n, r) => n + FIELDS.filter(([k, , t]) => fromInput(t, toInput(t, r[k])) == null || (t === 'list' && !(r[k] || []).length)).length, 0);

  return (
    <div className="mt-3 pt-3 border-t space-y-3" data-testid={`program-review-${sid}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          Review {rows.length} extracted program(s) — {notConfirmedCount} field(s) Not Confirmed. Edit or leave blank; nothing is saved until you approve.
        </p>
        <Button size="sm" onClick={approve} disabled={saving} data-testid={`approve-programs-${sid}`}>
          <CheckCircle className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Approve selected → Product Matrix'}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs min-w-[1400px]">
          <thead>
            <tr className="text-muted-foreground text-left">
              <th className="p-1">Use</th>
              {FIELDS.map(([k, label]) => <th key={k} className="p-1 whitespace-nowrap">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <React.Fragment key={r.program_id || i}>
              <tr className="border-t align-top" data-testid={`review-row-${i}`}>
                <td className="p-1 pt-2">
                  <Checkbox checked={r._selected} onCheckedChange={(v) => setRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, _selected: !!v } : x)))} data-testid={`review-row-${i}-select`} />
                </td>
                {FIELDS.map(([k, , type]) => {
                  const val = toInput(type, r[k]);
                  return (
                    <td key={k} className="p-1">
                      <Input
                        value={val}
                        placeholder="Not Confirmed"
                        onChange={(e) => update(i, k, type, e.target.value)}
                        className={`h-7 text-xs min-w-[90px] ${val === '' ? 'border-amber-400 bg-amber-50 placeholder:text-amber-600' : ''}`}
                        data-testid={`review-row-${i}-${k}`}
                      />
                    </td>
                  );
                })}
              </tr>
              {(r.source_excerpt || (r.ambiguities || []).length > 0) && (
                <tr className="align-top" data-testid={`review-row-${i}-meta`}>
                  <td />
                  <td colSpan={FIELDS.length} className="p-1 pb-2 space-y-0.5">
                    {r.source_excerpt && <p className="text-[11px] text-muted-foreground italic">Source: “{r.source_excerpt}”</p>}
                    {(r.ambiguities || []).length > 0 && (
                      <p className="text-[11px] text-amber-700"><AlertTriangle className="inline h-3 w-3 mr-1" />Confirm with lender: {r.ambiguities.join(' · ')}</p>
                    )}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        <Badge variant="outline" className="mr-1 border-amber-400 text-amber-700">Not Confirmed</Badge>
        values stay blank in the matrix and are reported as “not confirmed” during lead matching — they are never estimated.
      </p>
    </div>
  );
};

export default ProgramReviewTable;
